"use client"

import { useState, useEffect } from "react"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import Snackbar from "@mui/material/Snackbar"
import Alert from "@mui/material/Alert"
import { useNavigate } from "react-router-dom"
import { Button, Box, Tab, Tabs, Menu, MenuItem, Divider, Typography } from "@mui/material"
import AllQuestions from "./AllQuestions"

function Main({ questions, isTagDetailsPage = false, pageType = "all" }) {
  const user = JSON.parse(localStorage.getItem("UserDetails"))
  const isPortalView = JSON.parse(localStorage.getItem("isPortalView"))
  const iframe = localStorage.getItem("iframe") === "true";
  const navigate = useNavigate()
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState("info")

  // Filter and sort state
  const [tabValue, setTabValue] = useState(0)
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null)
  const [filteredQuestions, setFilteredQuestions] = useState(questions)
  const [activeFilters, setActiveFilters] = useState({
    tab: "all", // all, answered, unanswered
    more: null, // verified, bianVerified
    sortBy: "latest", // latest, votes, week, month
  })

  // Add responsive handling
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Update filtered questions when questions or filters change
  useEffect(() => {
    if (!questions || questions.length === 0) {
      setFilteredQuestions([])
      return
    }

    let result = [...questions]

    // Reset more filter when switching to "All" tab
    if (activeFilters.tab === "all" && activeFilters.more !== null) {
      setActiveFilters((prev) => ({ ...prev, more: null }))
      return // Wait for the next render with updated state
    }

    // Apply tab filter
    switch (activeFilters.tab) {
      case "answered":
        result = result.filter((q) => q.answerDetails && q.answerDetails.length > 0)
        break
      case "unanswered":
        result = result.filter((q) => !q.answerDetails || q.answerDetails.length === 0)
        break
      case "all":
      default:
        // No tab filtering for "all"
        break
    }

    // Apply more filter only if set
    if (activeFilters.more) {
      switch (activeFilters.more) {
        case "verified":
          result = result.filter((q) =>
            q.answerDetails && q.answerDetails.some((a) => a.verification && a.verification.verified)
          )
          break
        case "bianVerified":
          result = result.filter((q) =>
            q.answerDetails &&
            q.answerDetails.some((a) =>
              a.verification &&
              a.verification.verified &&
              a.user &&
              a.user.organisation &&
              a.user.organisation.name === "BIAN"
            )
          )
          break
      }
    }

    // Calculate total votes for sorting
    const calculateTotalVotes = (question) => {
      const questionVotes = question.voteCount || 0
      const aiVotes = question.aiVoteCount || 0
      const answerVotes = question.answerDetails?.reduce((sum, answer) => sum + (answer.voteCount || 0), 0) || 0
      return questionVotes + aiVotes + answerVotes
    }

    // Apply sorting
    switch (activeFilters.sortBy) {
      case "votes":
        result.sort((a, b) => calculateTotalVotes(b) - calculateTotalVotes(a))
        break
      case "week":
        const oneWeekAgo = new Date()
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
        result = result.filter((q) => new Date(q.created_at) >= oneWeekAgo)
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        break
      case "month":
        const oneMonthAgo = new Date()
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
        result = result.filter((q) => new Date(q.created_at) >= oneMonthAgo)
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        break
      case "latest":
      default:
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        break
    }

    setFilteredQuestions(result)
  }, [questions, activeFilters.tab, activeFilters.more, activeFilters.sortBy])

  const isMobile = windowWidth <= 768

  const handleAddQuestionClick = () => {
    if (!user) {
      setSnackbarMessage("You must be logged in to ask a question.")
      setSnackbarSeverity("warning")
      setOpenSnackbar(true)
      // setTimeout(() => navigate("/login"), 1500)
    } else if( localStorage.getItem("iframe") !== "true" && isPortalView ){
      setSnackbarMessage("You must be logged in to ask a question.")
      setSnackbarSeverity("warning")
      setOpenSnackbar(true)
      // setTimeout(() => navigate("/login"), 1500)
      
    } else {
      navigate("/add-question")
    }
    
    // if (!user) {
    //   setSnackbarMessage("You must be logged in to ask a question.")
    //   setSnackbarSeverity("warning")
    //   setOpenSnackbar(true)
    //   setTimeout(() => navigate("/login"), 1500)
    // } else {
    //   navigate("/add-question")
    // }
  }

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false)
  }

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
    const tabFilters = ["all", "answered", "unanswered"]
    setActiveFilters({
      ...activeFilters,
      tab: tabFilters[newValue],
      more: newValue === 0 ? null : activeFilters.more, // Reset more filter when switching to "All"
    })
  }

  // Handle More menu
  const handleMoreClick = (event) => {
    setMoreMenuAnchorEl(event.currentTarget)
  }

  const handleMoreClose = () => {
    setMoreMenuAnchorEl(null)
  }

  const handleMoreOptionClick = (option) => {
    if (option === activeFilters.more) {
      setActiveFilters({
        ...activeFilters,
        more: null,
      })
    } else {
      setActiveFilters({
        ...activeFilters,
        more: option,
      })
    }
    handleMoreClose()
  }

  // Handle sort option
  const handleSortOptionClick = (option) => {
    setActiveFilters({
      ...activeFilters,
      sortBy: option,
    })
    handleMoreClose()
  }

  return (
    <Box sx={{ padding: { xs: "16px 8px", sm: "24px 16px" } }}>
      <Box>
        {!isTagDetailsPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              marginBottom: "12px",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: "12px", sm: 0 },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "22px", sm: "27px" },
                fontWeight: 400,
                color: "#232629",
                margin: 0,
              }}
            >
              {pageType === "home" ? "Interesting Posts for You" : "All Questions"}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddQuestionClick}
              sx={{
                borderRadius: 4,
                backgroundColor: "#0a95ff",
                "&:hover": {
                  backgroundColor: "#0074cc",
                },
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Ask Question
            </Button>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            marginBottom: "12px",
            padding: "12px 0",
            borderBottom: "1px solid #e3e6e8",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: "12px", sm: 0 },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: "17px",
              color: "#232629",
            }}
          >
            {filteredQuestions.length} questions
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, sm: 2 },
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Tab filters */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                minHeight: "32px",
                "& .MuiTab-root": {
                  minHeight: "32px",
                  padding: "6px 12px",
                  textTransform: "none",
                  fontSize: { xs: "12px", sm: "13px" },
                  color: "#6a737c",
                  "&.Mui-selected": {
                    backgroundColor: "#e3e6e8",
                    color: "#3b4045",
                    borderRadius: "3px",
                  },
                },
                "& .MuiTabs-indicator": {
                  display: "none",
                },
              }}
            >
              <Tab label="All" />
              <Tab label="Answered" />
              <Tab label="Unanswered" />
            </Tabs>

            {/* More dropdown */}
            <Button
              onClick={handleMoreClick}
              endIcon={moreMenuAnchorEl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                textTransform: "none",
                fontSize: { xs: "12px", sm: "13px" },
                color: "#6a737c",
                border: "1px solid #d6d9dc",
                padding: "6px 12px",
                minWidth: "auto",
                borderRadius: "3px",
                backgroundColor: activeFilters.more ? "#e3e6e8" : "transparent",
                "&:hover": {
                  backgroundColor: activeFilters.more ? "#d6d9dc" : "#f8f9fa",
                },
              }}
            >
              More
            </Button>
            <Menu
              anchorEl={moreMenuAnchorEl}
              open={Boolean(moreMenuAnchorEl)}
              onClose={handleMoreClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  width: 200,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.06), 0 3px 8px rgba(0,0,0,0.09)",
                  borderRadius: "3px",
                  border: "1px solid #d6d9dc",
                },
              }}
            >
              <MenuItem
                onClick={() => handleMoreOptionClick("verified")}
                sx={{
                  fontSize: { xs: "12px", sm: "13px" },
                  backgroundColor: activeFilters.more === "verified" ? "#f1f2f3" : "transparent",
                  "&:hover": { backgroundColor: activeFilters.more === "verified" ? "#e3e6e8" : "#f8f9fa" },
                }}
              >
                Verified
              </MenuItem>
              <MenuItem
                onClick={() => handleMoreOptionClick("bianVerified")}
                sx={{
                  fontSize: { xs: "12px", sm: "13px" },
                  backgroundColor: activeFilters.more === "bianVerified" ? "#f1f2f3" : "transparent",
                  "&:hover": { backgroundColor: activeFilters.more === "bianVerified" ? "#e3e6e8" : "#f8f9fa" },
                }}
              >
                BIAN Verified
              </MenuItem>
              <Divider />
              <Box sx={{ px: 1.5, py: 0.5 }}>
                <Typography variant="caption" sx={{ color: "#6a737c", fontWeight: "bold", textTransform: "uppercase" }}>
                  SORT BY
                </Typography>
              </Box>
              <MenuItem
                onClick={() => handleSortOptionClick("latest")}
                sx={{
                  fontSize: { xs: "12px", sm: "13px" },
                  backgroundColor: activeFilters.sortBy === "latest" ? "#f1f2f3" : "transparent",
                  "&:hover": { backgroundColor: activeFilters.sortBy === "latest" ? "#e3e6e8" : "#f8f9fa" },
                }}
              >
                Latest
              </MenuItem>
              <MenuItem
                onClick={() => handleSortOptionClick("votes")}
                sx={{
                  fontSize: { xs: "12px", sm: "13px" },
                  backgroundColor: activeFilters.sortBy === "votes" ? "#f1f2f3" : "transparent",
                  "&:hover": { backgroundColor: activeFilters.sortBy === "votes" ? "#e3e6e8" : "#f8f9fa" },
                }}
              >
                Most Votes
              </MenuItem>
              <MenuItem
                onClick={() => handleSortOptionClick("week")}
                sx={{
                  fontSize: { xs: "12px", sm: "13px" },
                  backgroundColor: activeFilters.sortBy === "week" ? "#f1f2f3" : "transparent",
                  "&:hover": { backgroundColor: activeFilters.sortBy === "week" ? "#e3e6e8" : "#f8f9fa" },
                }}
              >
                This Week
              </MenuItem>
              <MenuItem
                onClick={() => handleSortOptionClick("month")}
                sx={{
                  fontSize: { xs: "12px", sm: "13px" },
                  backgroundColor: activeFilters.sortBy === "month" ? "#f1f2f3" : "transparent",
                  "&:hover": { backgroundColor: activeFilters.sortBy === "month" ? "#e3e6e8" : "#f8f9fa" },
                }}
              >
                This Month
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Questions */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1, sm: 2 },
          }}
        >
          {filteredQuestions.length > 0 ? (
            filteredQuestions.map((q) => (
              <Box key={q._id || q.id}>
                <AllQuestions data={q} />
              </Box>
            ))
          ) : (
            <Box
              sx={{
                padding: "48px 0",
                textAlign: "center",
                color: "#6a737c",
                fontSize: { xs: "14px", sm: "15px" },
              }}
            >
              No questions found matching your criteria.
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Main




// "use client";

// import { useState, useEffect } from "react";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import ExpandLessIcon from "@mui/icons-material/ExpandLess";
// import Snackbar from "@mui/material/Snackbar";
// import Alert from "@mui/material/Alert";
// import { useNavigate } from "react-router-dom";
// import moment from "moment";
// import AllQuestions from "./AllQuestions";
// import { Button } from "@mui/material";

// function Main({ questions, isTagDetailsPage = false, pageType = "all" }) {
//   const user = JSON.parse(localStorage.getItem("UserDetails"));
//   const navigate = useNavigate();
//   const [windowWidth, setWindowWidth] = useState(window.innerWidth);

//   const [openSnackbar, setOpenSnackbar] = useState(false);
//   const [snackbarMessage, setSnackbarMessage] = useState("");
//   const [snackbarSeverity, setSnackbarSeverity] = useState("info");
//   const [filter, setFilter] = useState("all");
//   const [sortBy, setSortBy] = useState("latest");
//   const [showMore, setShowMore] = useState(false);

//   // Add responsive handling
//   useEffect(() => {
//     const handleResize = () => {
//       setWindowWidth(window.innerWidth);
//     };
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const isMobile = windowWidth <= 768;

//   const handleAddQuestionClick = () => {
//     if (!user) {
//       setSnackbarMessage("You must be logged in to ask a question.");
//       setSnackbarSeverity("warning");
//       setOpenSnackbar(true);
//       setTimeout(() => navigate("/login"), 1500);
//     } else {
//       navigate("/add-question");
//     }
//   };

//   const handleCloseSnackbar = () => {
//     setOpenSnackbar(false);
//   };

//   const toggleMore = () => {
//     setShowMore(!showMore);
//   };

//   const filteredQuestions = questions.filter((q) => {
//     const answerCount = Array.isArray(q.answerDetails)
//       ? q.answerDetails.length
//       : 0;
//     const createdAt = moment(q.created_at);
//     const isWeek = moment().diff(createdAt, "days") <= 7;
//     const isMonth = moment().diff(createdAt, "days") <= 30;

//     let match = true;

//     if (filter === "answered") match = answerCount > 0;
//     if (filter === "unanswered") match = answerCount === 0;
//     if (filter === "verified") match = q.verified === true;
//     if (filter === "bianVerified") match = q.bian_verified === true;

//     if (sortBy === "week") return match && isWeek;
//     if (sortBy === "month") return match && isMonth;

//     return match;
//   });

//   return (
//     <div style={{ padding: isMobile ? "16px 8px" : "24px 16px" }}>
//       <div>
//         {!isTagDetailsPage && (
//           <div
//             style={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: isMobile ? "flex-start" : "center",
//               marginBottom: "12px",
//               flexDirection: isMobile ? "column" : "row",
//               gap: isMobile ? "12px" : "0",
//             }}
//           >
//             <h1
//               style={{
//                 fontSize: isMobile ? "22px" : "27px",
//                 fontWeight: "400",
//                 color: "#232629",
//                 margin: 0,
//               }}
//             >
//               {pageType === "home"
//                 ? "Interesting Posts for You"
//                 : "All Questions"}
//             </h1>
//             {/* <button
//               onClick={handleAddQuestionClick}
//               style={{
//                 backgroundColor: "#0a95ff", // Matches the blue color in the image
//                 color: "white",
//                 border: "none", // Remove border to match the image's solid fill
//                 padding: "8px 16px", // Adjusted padding for a wider, oval shape
//                 borderRadius: "9999px", // Large radius for a pill/oval shape
//                 cursor: "pointer",
//                 fontSize: "14px", // Slightly larger font to match the image
//                 fontWeight: "500", // Medium weight for better readability
//                 boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", // Subtle shadow to match the elevated look
//                 transition: "background-color 0.2s", // Smooth hover transition
//               }}
//               onMouseOver={(e) => {
//                 e.currentTarget.style.backgroundColor = "#0074cc"; // Darker blue on hover
//               }}
//               onMouseOut={(e) => {
//                 e.currentTarget.style.backgroundColor = "#0a95ff"; // Reset to original color
//               }}
//             >
//               Ask Question
//             </button> */}
//             <Button
//               variant="contained"
//               color="primary"
//               onClick={handleAddQuestionClick}
//               sx={{ borderRadius: 4 }}
//             >
//               Ask Question
//             </Button>
//           </div>
//         )}

//         <div
//           style={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: isMobile ? "flex-start" : "center",
//             marginBottom: "12px",
//             padding: "12px 0",
//             borderBottom: "1px solid #e3e6e8",
//             flexDirection: isMobile ? "column" : "row",
//             gap: isMobile ? "12px" : "0",
//           }}
//         >
//           <div
//             style={{
//               fontSize: "17px",
//               color: "#232629",
//             }}
//           >
//             {filteredQuestions.length} questions
//           </div>

//           <div
//             style={{
//               display: "flex",
//               gap: "4px",
//               alignItems: "center",
//               flexWrap: "wrap",
//             }}
//           >
//             <div
//               onClick={() => setFilter("all")}
//               style={{
//                 cursor: "pointer",
//                 padding: "6px 12px",
//                 borderRadius: "3px",
//                 fontSize: "13px",
//                 color: filter === "all" ? "#3b4045" : "#6a737c",
//                 backgroundColor: filter === "all" ? "#e3e6e8" : "transparent",
//                 border:
//                   filter === "all"
//                     ? "1px solid #d6d9dc"
//                     : "1px solid transparent",
//               }}
//             >
//               All
//             </div>

//             <div
//               onClick={() => setFilter("answered")}
//               style={{
//                 cursor: "pointer",
//                 padding: "6px 12px",
//                 borderRadius: "3px",
//                 fontSize: "13px",
//                 color: filter === "answered" ? "#3b4045" : "#6a737c",
//                 backgroundColor:
//                   filter === "answered" ? "#e3e6e8" : "transparent",
//                 border:
//                   filter === "answered"
//                     ? "1px solid #d6d9dc"
//                     : "1px solid transparent",
//               }}
//             >
//               Answered
//             </div>

//             <div
//               onClick={() => setFilter("unanswered")}
//               style={{
//                 cursor: "pointer",
//                 padding: "6px 12px",
//                 borderRadius: "3px",
//                 fontSize: "13px",
//                 color: filter === "unanswered" ? "#3b4045" : "#6a737c",
//                 backgroundColor:
//                   filter === "unanswered" ? "#e3e6e8" : "transparent",
//                 border:
//                   filter === "unanswered"
//                     ? "1px solid #d6d9dc"
//                     : "1px solid transparent",
//               }}
//             >
//               Unanswered
//             </div>

//             <div style={{ position: "relative" }}>
//               <div
//                 onClick={toggleMore}
//                 style={{
//                   cursor: "pointer",
//                   display: "flex",
//                   alignItems: "center",
//                   padding: "6px 12px",
//                   borderRadius: "3px",
//                   fontSize: "13px",
//                   color: "#6a737c",
//                   border: "1px solid #d6d9dc",
//                 }}
//               >
//                 More{" "}
//                 {showMore ? (
//                   <ExpandLessIcon
//                     fontSize="small"
//                     style={{ marginLeft: "2px" }}
//                   />
//                 ) : (
//                   <ExpandMoreIcon
//                     fontSize="small"
//                     style={{ marginLeft: "2px" }}
//                   />
//                 )}
//               </div>

//               {showMore && (
//                 <div
//                   style={{
//                     position: "absolute",
//                     top: "100%",
//                     right: 0,
//                     marginTop: "8px",
//                     padding: "8px 0",
//                     background: "white",
//                     boxShadow:
//                       "0 1px 3px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.06), 0 3px 8px rgba(0,0,0,0.09)",
//                     borderRadius: "3px",
//                     width: "200px",
//                     zIndex: 100,
//                     border: "1px solid #d6d9dc",
//                   }}
//                 >
//                   <div
//                     onClick={() => setFilter("verified")}
//                     style={{
//                       cursor: "pointer",
//                       padding: "6px 12px",
//                       fontSize: "13px",
//                       color: filter === "verified" ? "#0c0d0e" : "#3b4045",
//                       backgroundColor:
//                         filter === "verified" ? "#f1f2f3" : "transparent",
//                       display: "block",
//                       width: "100%",
//                       textAlign: "left",
//                     }}
//                   >
//                     Verified
//                   </div>

//                   <div
//                     onClick={() => setFilter("bianVerified")}
//                     style={{
//                       cursor: "pointer",
//                       padding: "6px 12px",
//                       fontSize: "13px",
//                       color: filter === "bianVerified" ? "#0c0d0e" : "#3b4045",
//                       backgroundColor:
//                         filter === "bianVerified" ? "#f1f2f3" : "transparent",
//                       display: "block",
//                       width: "100%",
//                       textAlign: "left",
//                     }}
//                   >
//                     BIAN Verified
//                   </div>

//                   <div
//                     style={{
//                       padding: "6px 12px",
//                       fontSize: "11px",
//                       color: "#6a737c",
//                       fontWeight: "bold",
//                       textTransform: "uppercase",
//                       borderTop: "1px solid #e3e6e8",
//                       marginTop: "4px",
//                     }}
//                   >
//                     Sort by
//                   </div>

//                   <div
//                     onClick={() => setSortBy("latest")}
//                     style={{
//                       cursor: "pointer",
//                       padding: "6px 12px",
//                       fontSize: "13px",
//                       color: sortBy === "latest" ? "#0c0d0e" : "#3b4045",
//                       backgroundColor:
//                         sortBy === "latest" ? "#f1f2f3" : "transparent",
//                       display: "block",
//                       width: "100%",
//                       textAlign: "left",
//                     }}
//                   >
//                     Latest
//                   </div>

//                   <div
//                     onClick={() => setSortBy("week")}
//                     style={{
//                       cursor: "pointer",
//                       padding: "6px 12px",
//                       fontSize: "13px",
//                       color: sortBy === "week" ? "#0c0d0e" : "#3b4045",
//                       backgroundColor:
//                         sortBy === "week" ? "#f1f2f3" : "transparent",
//                       display: "block",
//                       width: "100%",
//                       textAlign: "left",
//                     }}
//                   >
//                     This Week
//                   </div>

//                   <div
//                     onClick={() => setSortBy("month")}
//                     style={{
//                       cursor: "pointer",
//                       padding: "6px 12px",
//                       fontSize: "13px",
//                       color: sortBy === "month" ? "#0c0d0e" : "#3b4045",
//                       backgroundColor:
//                         sortBy === "month" ? "#f1f2f3" : "transparent",
//                       display: "block",
//                       width: "100%",
//                       textAlign: "left",
//                     }}
//                   >
//                     This Month
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Questions */}
//         <div
//           style={{
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           {filteredQuestions.length > 0 ? (
//             filteredQuestions.map((q) => (
//               <div key={q._id || q.id}>
//                 <AllQuestions data={q} />
//               </div>
//             ))
//           ) : (
//             <div
//               style={{
//                 padding: "48px 0",
//                 textAlign: "center",
//                 color: "#6a737c",
//                 fontSize: "15px",
//               }}
//             >
//               No questions found matching your criteria.
//             </div>
//           )}
//         </div>
//       </div>

//       <Snackbar
//         open={openSnackbar}
//         autoHideDuration={6000}
//         onClose={handleCloseSnackbar}
//         anchorOrigin={{ vertical: "top", horizontal: "center" }}
//       >
//         <Alert
//           onClose={handleCloseSnackbar}
//           severity={snackbarSeverity}
//           sx={{ width: "100%" }}
//           variant="filled"
//         >
//           {snackbarMessage}
//         </Alert>
//       </Snackbar>
//     </div>
//   );
// }

// export default Main;
